'use client';

import { Box, Paper, Stack, Typography } from '@mui/material';
import { Tooltip } from '@mui/material';
import { useAppColors } from '@/styles/colors';

type PricePoint = {
  date: string;
  close: number;
};

type RiskMomentCardProps = {
  priceSeries: PricePoint[];
  benchmarkSeries?: PricePoint[];
  tickerLabel?: string;
  title?: string;
};

const PERCENT_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function RiskMomentCard({
  priceSeries,
  benchmarkSeries = [],
  tickerLabel = 'Fundo',
  title = 'Risco e Momento',
}: RiskMomentCardProps) {
  const colors = useAppColors();
  const metrics = computeRiskMetrics(priceSeries);
  const benchmarkMetrics = computeRiskMetrics(benchmarkSeries);
  const tickerBadgeLabel = normalizeDisplayInstrument(tickerLabel);

  if (!metrics) {
    return (
      <Paper variant='outlined' sx={{ p: 2, borderStyle: 'dashed', color: 'text.secondary' }}>
        Sem dados suficientes para análise de risco no período.
      </Paper>
    );
  }

  const volatilityColor =
    metrics.volatilityLabel === 'Baixa'
      ? colors.modules.risk.good
      : metrics.volatilityLabel === 'Moderada'
        ? colors.modules.risk.medium
        : colors.modules.risk.bad;

  return (
    <Paper variant='outlined' sx={{ p: { xs: 1.5, md: 2 } }}>
      <Typography variant='h6' fontWeight={700}>
        {title}
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1.5, md: 3 }} sx={{ mt: 1.25, mb: 1.5 }}>
        <Metric
          label='Volatilidade anualizada (janela 21d)'
          value={`${PERCENT_FORMATTER.format(metrics.latestVol)}%`}
          color={volatilityColor}
          helpText='Mede o quanto a cotação oscila no curto prazo. É calculada com retornos diários no período exibido, usando janela móvel de 21 pregões e anualização por raiz de 252. Quanto maior, mais risco de variação no curto prazo.'
        />
        <Metric
          label='Faixa de preço no período'
          value={`${PERCENT_FORMATTER.format(metrics.priceRangePosition)}%`}
          helpText='Mostra onde o preço atual está dentro da faixa mínima-máxima do período selecionado. Próximo de 0% = perto da mínima do período; próximo de 100% = perto da máxima. Ajuda a avaliar o “momento” do preço.'
        />
        <Metric
          label='Max drawdown'
          value={`${PERCENT_FORMATTER.format(metrics.maxDrawdown)}%`}
          color={colors.modules.risk.bad}
          helpText='É a maior queda percentual desde um topo até o fundo seguinte dentro do período selecionado. Exemplo: -20% significa que em algum momento o preço caiu 20% a partir de um pico local.'
        />
      </Stack>

      <Box
        sx={{
          border: '1px solid hsl(var(--border))',
          borderRadius: 2,
          p: 1.2,
          bgcolor: 'hsl(var(--muted) / 0.35)',
        }}
      >
        <Stack direction='row' justifyContent='space-between' sx={{ mb: 0.65 }}>
          <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
            Termômetro de volatilidade
          </Typography>
          <Typography variant='caption' sx={{ fontWeight: 800, color: volatilityColor }}>
            {metrics.volatilityLabel}
          </Typography>
        </Stack>

        <Box
          sx={{
            position: 'relative',
            mt: 4.2,
            height: 8,
            borderRadius: 999,
            background: `linear-gradient(90deg, ${colors.modules.risk.good} 0%, ${colors.modules.risk.medium} 58%, ${colors.modules.risk.bad} 100%)`,
          }}
        >
          <Tooltip title={`${tickerBadgeLabel}: ${PERCENT_FORMATTER.format(metrics.latestVol)}%`} arrow placement='top'>
            <Box
              sx={{
                position: 'absolute',
                top: -24,
                left: `${metrics.volatilityPointerPct}%`,
                transform: 'translateX(-50%)',
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  px: 0.75,
                  py: 0.1,
                  borderRadius: 0.8,
                  bgcolor: colors.modules.risk.needle,
                  color: colors.semantic.white,
                  fontSize: 12,
                  fontWeight: 800,
                  lineHeight: 1.4,
                  letterSpacing: 0.2,
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 8px hsl(var(--foreground) / 0.2)',
                }}
              >
                {`${tickerBadgeLabel} ${PERCENT_FORMATTER.format(metrics.latestVol)}%`}
              </Box>
              <Box
                sx={{
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: `6px solid ${colors.modules.risk.needle}`,
                }}
              />
            </Box>
          </Tooltip>
          {benchmarkMetrics ? (
            <Tooltip title={`IFIX: ${PERCENT_FORMATTER.format(benchmarkMetrics.latestVol)}%`} arrow placement='top'>
              <Box
                sx={{
                  position: 'absolute',
                  top: 12,
                  left: `${benchmarkMetrics.volatilityPointerPct}%`,
                  transform: 'translateX(-50%)',
                  display: 'inline-flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 0,
                    height: 0,
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderBottom: `6px solid ${colors.modules.risk.needleBorder}`,
                  }}
                />
                <Box
                  sx={{
                    px: 0.75,
                    py: 0.1,
                    borderRadius: 0.8,
                    bgcolor: colors.modules.risk.needleBorder,
                    color: colors.semantic.white,
                    fontSize: 12,
                    fontWeight: 800,
                    lineHeight: 1.4,
                    letterSpacing: 0.2,
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 8px hsl(var(--foreground) / 0.2)',
                  }}
                >
                  {`IFIX ${PERCENT_FORMATTER.format(benchmarkMetrics.latestVol)}%`}
                </Box>
              </Box>
            </Tooltip>
          ) : null}
        </Box>

        <Stack direction='row' justifyContent='space-between' sx={{ mt: benchmarkMetrics ? 3.2 : 0.5 }}>
          <Typography variant='caption' color='text.secondary'>
            0%
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {PERCENT_FORMATTER.format(metrics.volatilityScaleMax)}%
          </Typography>
        </Stack>
      </Box>
    </Paper>
  );
}

function normalizeDisplayInstrument(value: string) {
  const raw = value.trim().toUpperCase();
  if (/^[A-Z]{4}$/.test(raw)) return `${raw}11`;
  return raw || 'FUNDO';
}

function computeRiskMetrics(series: PricePoint[]) {
  if (series.length < 3) return null;

  const ordered = [...series].sort((a, b) => a.date.localeCompare(b.date));
  const closes = ordered.map((item) => item.close).filter((value) => Number.isFinite(value) && value > 0);
  if (closes.length < 3) return null;

  const dailyReturns: number[] = [];
  for (let index = 1; index < closes.length; index += 1) {
    const prev = closes[index - 1];
    const current = closes[index];
    if (prev <= 0) continue;
    dailyReturns.push(current / prev - 1);
  }
  if (dailyReturns.length < 2) return null;

  const windowSize = 21;
  const rollingVol: number[] = [];
  for (let index = windowSize - 1; index < dailyReturns.length; index += 1) {
    const window = dailyReturns.slice(index - (windowSize - 1), index + 1);
    rollingVol.push(stdDev(window) * Math.sqrt(252) * 100);
  }
  const latestVol =
    rollingVol.length > 0 ? rollingVol[rollingVol.length - 1] : stdDev(dailyReturns) * Math.sqrt(252) * 100;
  const maxVol = rollingVol.length > 0 ? Math.max(...rollingVol) : latestVol;
  const volatilityScaleMax = Math.max(40, Math.ceil(maxVol / 10) * 10);
  const volatilityPointerPct = clamp((latestVol / volatilityScaleMax) * 100, 0, 100);

  const minPrice = Math.min(...closes);
  const maxPrice = Math.max(...closes);
  const lastPrice = closes[closes.length - 1];
  const priceRangePosition = maxPrice > minPrice ? ((lastPrice - minPrice) / (maxPrice - minPrice)) * 100 : 50;

  let peak = closes[0];
  let maxDrawdown = 0;
  for (const close of closes) {
    peak = Math.max(peak, close);
    const drawdown = ((close - peak) / peak) * 100;
    maxDrawdown = Math.min(maxDrawdown, drawdown);
  }

  const volatilityLabel = latestVol < 15 ? 'Baixa' : latestVol < 25 ? 'Moderada' : 'Alta';

  return {
    latestVol,
    volatilityScaleMax,
    volatilityPointerPct,
    priceRangePosition,
    maxDrawdown,
    volatilityLabel,
  };
}

function stdDev(values: number[]) {
  if (values.length === 0) return 0;
  const mean = values.reduce((acc, value) => acc + value, 0) / values.length;
  const variance = values.reduce((acc, value) => acc + (value - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function Metric({
  label,
  value,
  color,
  helpText,
}: {
  label: string;
  value: string;
  color?: string;
  helpText?: string;
}) {
  return (
    <Stack spacing={0.2}>
      <Stack direction='row' spacing={0.5} alignItems='center'>
        <Typography variant='caption' color='text.secondary'>
          {label}
        </Typography>
        {helpText ? <HelpDotTooltip text={helpText} /> : null}
      </Stack>
      <Typography variant='h6' fontWeight={800} sx={{ color: color ?? 'text.primary' }}>
        {value}
      </Typography>
    </Stack>
  );
}

function HelpDotTooltip({ text }: { text: string }) {
  return (
    <Tooltip title={text} arrow placement='top'>
      <Box
        component='span'
        sx={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          border: '1px solid hsl(var(--border))',
          color: 'text.secondary',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 800,
          lineHeight: 1,
          cursor: 'help',
          userSelect: 'none',
        }}
        aria-label='Metodologia da métrica'
      >
        ?
      </Box>
    </Tooltip>
  );
}
